import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
    ArrowRight, Sparkles, Users, Calendar, Briefcase, FileText, BarChart3, ShieldCheck, 
    Github, Linkedin, Twitter, Instagram, Sun, Moon, Loader2, Star, Check 
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useTheme } from "../contexts/ThemeContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import api from "../lib/api";
import { toast } from "sonner";

const FEATURES = [
    { icon: Users, title: "People at the core", body: "Profiles, departments, designations and reporting structures — all auto-organised." },
    { icon: Calendar, title: "Attendance & leaves", body: "Punch-in with GPS, smart leave policies, balances tracked in real-time." },
    { icon: Briefcase, title: "Hire smarter", body: "A drag-and-drop pipeline from applied to selected, with interview scheduling." },
    { icon: FileText, title: "Letters in one click", body: "Auto-generate offer, joining and relieving letters as polished PDFs." },
    { icon: BarChart3, title: "Live analytics", body: "Headcount, attendance heatmaps and pending actions on a glanceable dashboard." },
    { icon: ShieldCheck, title: "Role-based security", body: "Super admin, HR, manager, employee and intern roles with strict scoping." },
];

const FAQS = [
    { q: "How does the 14-day free trial work?", a: "Registering an admin account automatically starts a free trial for 14 days (up to 10 employees limit for testing). You can test geofenced attendance, document generation, and chat services without a credit card. You can upgrade to Pro at any time." },
    { q: "What security measures protect my organization's data?", a: "We enforce complete physical database isolation (Database-per-Tenant model). Each workspace operates in its own isolated database instance. With strict role-based access control (RBAC), thread-safe scoping middleware, and secure JWT-only claims, data leakage is impossible." },
    { q: "What happens after the trial period ends?", a: "When your trial ends, the dashboard displays a blocked prompt screen. All records, documents, and attendance sheets are securely preserved; once you subscribe to the Pro plan, your workspace is instantly unlocked." },
    { q: "What are the payment terms and subscription costs?", a: "We charge a flat ₹5,000 + 18% GST (total ₹5,900.00) monthly fee for up to 100 active employees and interns. For teams larger than 100, we offer custom dedicated server instances with customized pricing." },
    { q: "How is geofenced location tracking handled?", a: "To respect employee privacy, location coordinates are strictly queried only when clocking in or out via the mobile portal. Background GPS polling is never performed, and coordinates are encrypted inside your tenant database." }
];

export default function Landing() {
    const { theme, toggle } = useTheme();
    const [modal, setModal] = useState(null);
    const [activeFaq, setActiveFaq] = useState(null);
    return (
        <div className="min-h-screen bg-background relative overflow-hidden">
            <div aria-hidden className="absolute -top-40 -left-20 w-[420px] h-[420px] rounded-full bg-primary/30 blur-3xl animate-blob-float" />
            <div aria-hidden className="absolute top-40 right-0 w-[480px] h-[480px] rounded-full bg-accent/20 blur-3xl animate-blob-float" style={{ animationDelay: "8s" }} />

            {/* nav */}
            <header className="relative z-10 px-6 lg:px-12 h-20 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-white grid place-items-center shadow-[0_0_20px_rgba(255,87,51,0.15)] overflow-hidden p-1">
                        <img src="/logo.png" alt="ParshHR Logo" className="w-full h-full object-contain" />
                    </div>
                    <span className="font-heading font-bold text-xl tracking-tight">ParshHR</span>
                </Link>
                <nav className="hidden md:flex items-center gap-8 text-sm">
                    <a href="#features" className="text-foreground/70 hover:text-foreground">Features</a>
                    <Link to="/careers" className="text-foreground/70 hover:text-foreground" data-testid="careers-link">Careers</Link>
                    <a href="#pricing" className="text-foreground/70 hover:text-foreground">Pricing</a>
                </nav>
                <div className="flex items-center gap-2">
                    <button onClick={toggle} className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" data-testid="landing-theme">
                        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    </button>
                    <Button asChild variant="ghost" className="rounded-full" data-testid="landing-login">
                        <Link to="/login">Sign in</Link>
                    </Button>
                    <Button asChild className="rounded-full" data-testid="landing-register">
                        <Link to="/register">Get started</Link>
                    </Button>
                </div>
            </header>

            {/* hero */}
            <section className="relative z-10 px-6 lg:px-12 pt-12 pb-24">
                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    {/* Left info column */}
                    <div className="lg:col-span-7 text-left space-y-6">
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs font-semibold">
                            <Sparkles className="w-3.5 h-3.5 text-primary animate-pulse" />
                            14 Days Free Trial &middot; No Credit Card
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]"
                        >
                            The Simplest HR Software to Automate <span className="text-gradient-primary">Operations</span>.
                        </motion.h1>
                        
                        <motion.p
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                            className="text-base sm:text-lg text-muted-foreground leading-relaxed"
                        >
                            From hire to retire &mdash; ParshHR handles onboarding, attendance with geofenced tracking, recruitment stages, leave approvals, and relational PDF letters in one unified platform.
                        </motion.p>

                        {/* Badges/Ratings */}
                        <motion.div 
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                            className="pt-4 grid grid-cols-2 gap-4 max-w-sm"
                        >
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-orange-500/10 grid place-items-center text-orange-500 font-bold text-sm">G</div>
                                <div>
                                    <div className="flex items-center gap-0.5 text-orange-500">
                                        {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current animate-pulse" />)}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground font-semibold">4.8/5 on Gartner</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-blue-500/10 grid place-items-center text-blue-500 font-bold text-sm">G2</div>
                                <div>
                                    <div className="flex items-center gap-0.5 text-blue-500">
                                        {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current animate-pulse" />)}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground font-semibold">4.8/5 on G2 Crowd</div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="flex items-center gap-3 pt-2">
                            <Button asChild size="lg" className="rounded-full h-12 px-7 text-base shadow-[0_0_24px_rgba(255,87,51,0.25)]" data-testid="hero-get-started">
                                <Link to="/register">
                                    Start 14-day Free Trial <ArrowRight className="ml-2 w-4 h-4" />
                                </Link>
                            </Button>
                        </motion.div>
                    </div>

                    {/* Right demo booking form column */}
                    <div className="lg:col-span-5">
                        <DemoBookingCard />
                    </div>
                </div>

                {/* preview card */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}
                    className="max-w-6xl mx-auto mt-20"
                >
                    <div className="glass rounded-3xl p-2 shadow-2xl overflow-hidden">
                        <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-accent/10 to-transparent p-8 lg:p-12">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                {[
                                    ["Total Employees", "248"],
                                    ["Attendance today", "212"],
                                    ["Pending leaves", "07"],
                                    ["Open roles", "12"],
                                ].map(([k, v], i) => (
                                    <motion.div key={k} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.07 }} className="glass rounded-2xl p-5">
                                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</div>
                                        <div className="font-heading font-bold text-3xl mt-2">{v}</div>
                                    </motion.div>
                                ))}
                            </div>
                            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                                <div className="glass rounded-2xl p-5 lg:col-span-2 h-48 flex items-end gap-2">
                                    {[40, 65, 50, 80, 70, 95, 85].map((h, i) => (
                                        <div key={i} className="flex-1 rounded-md bg-gradient-to-t from-primary to-accent" style={{ height: `${h}%` }} />
                                    ))}
                                </div>
                                <div className="glass rounded-2xl p-5">
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Recent activity</div>
                                    {["Aisha applied for Backend Eng.", "Devansh moved to Interview", "Priya approved 2 leaves"].map((l) => (
                                        <div key={l} className="flex items-center gap-2 text-sm py-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full bg-primary" /> <span className="truncate">{l}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* features */}
            <section id="features" className="relative z-10 px-6 lg:px-12 py-24 bg-black/[0.02] dark:bg-white/[0.02]">
                <div className="max-w-6xl mx-auto">
                    <div className="max-w-2xl">
                        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-3">Everything you need</div>
                        <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">All your HR ops, beautifully unified.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
                        {FEATURES.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <motion.div
                                    key={f.title}
                                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                                    className="glass glass-hover rounded-2xl p-6"
                                >
                                    <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center mb-4">
                                        <Icon className="w-5 h-5" strokeWidth={1.7} />
                                    </div>
                                    <h3 className="font-heading text-lg font-semibold mb-1.5">{f.title}</h3>
                                    <p className="text-sm text-muted-foreground">{f.body}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* pricing section */}
            <section id="pricing" className="relative z-10 px-6 lg:px-12 py-24 border-t border-black/5 dark:border-white/5">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-3">Transparent Plans</div>
                        <h2 className="font-heading text-4xl font-bold tracking-tight">Flexible plans for growing enterprises</h2>
                        <p className="text-xs text-muted-foreground mt-2">All features included. No hidden setup fees or surprise per-user charges.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Free Trial Plan Card */}
                        <div className="glass rounded-3xl p-8 border border-white/20 dark:border-white/5 flex flex-col justify-between relative overflow-hidden">
                            <div className="space-y-6">
                                <div>
                                    <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">Free Trial</h3>
                                    <p className="text-xs text-muted-foreground mt-1">Perfect for evaluating all platform tools.</p>
                                </div>
                                <div className="flex items-baseline gap-1">
                                    <span className="font-heading font-bold text-4xl text-slate-800 dark:text-slate-100">₹0</span>
                                    <span className="text-xs text-muted-foreground">/ 14 days</span>
                                </div>
                                <ul className="space-y-3 text-xs text-slate-600 dark:text-slate-300">
                                    <li className="flex items-center gap-2">✓ Up to 10 Active Members</li>
                                    <li className="flex items-center gap-2">✓ Full HR Dashboard Tools</li>
                                    <li className="flex items-center gap-2">✓ Live Attendance & GPS logs</li>
                                    <li className="flex items-center gap-2">✓ Basic Documents Manager</li>
                                    <li className="flex items-center gap-2">✓ No Credit Card Required</li>
                                </ul>
                            </div>
                            <Button asChild className="w-full rounded-full h-11 text-xs font-bold mt-8" variant="outline">
                                <Link to="/register">Start 14-day Trial</Link>
                            </Button>
                        </div>

                        {/* Pro Plan Card - RECOMMENDED */}
                        <div className="glass rounded-3xl p-8 border-2 border-primary flex flex-col justify-between relative overflow-hidden shadow-2xl">
                            <div className="absolute top-0 right-0 bg-primary text-white text-[10px] uppercase font-bold tracking-wider px-3.5 py-1 rounded-bl-xl">
                                Recommended
                            </div>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">Pro Scale</h3>
                                    <p className="text-xs text-muted-foreground mt-1">Complete HR suite for corporate offices.</p>
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-baseline gap-1">
                                        <span className="font-heading font-bold text-4xl text-slate-800 dark:text-slate-100">₹5,000</span>
                                        <span className="text-xs text-muted-foreground">/ month</span>
                                    </div>
                                    <div className="text-[10px] text-emerald-500 font-semibold">+18% GST (₹5,900/mo total cost)</div>
                                </div>
                                <ul className="space-y-3 text-xs text-slate-600 dark:text-slate-300 font-medium">
                                    <li className="flex items-center gap-2 text-emerald-600 dark:text-teal-400">✓ Up to 100 Active Members</li>
                                    <li className="flex items-center gap-2">✓ Physical Database Isolation</li>
                                    <li className="flex items-center gap-2">✓ Geofenced Live GPS Tracking</li>
                                    <li className="flex items-center gap-2">✓ PDF Letter Builders & NDAs</li>
                                    <li className="flex items-center gap-2">✓ Direct Slack/Chat support</li>
                                </ul>
                            </div>
                            <Button asChild className="w-full rounded-full h-11 text-xs font-bold mt-8 shadow-lg shadow-primary/30">
                                <Link to="/register">Upgrade Workspace</Link>
                            </Button>
                        </div>

                        {/* Custom / Enterprise Card */}
                        <div className="glass rounded-3xl p-8 border border-white/20 dark:border-white/5 flex flex-col justify-between relative overflow-hidden">
                            <div className="space-y-6">
                                <div>
                                    <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">Enterprise</h3>
                                    <p className="text-xs text-muted-foreground mt-1">Tailored for large scales & custom integrations.</p>
                                </div>
                                <div className="flex items-baseline gap-1">
                                    <span className="font-heading font-bold text-3xl text-slate-800 dark:text-slate-100">Custom</span>
                                    <span className="text-xs text-muted-foreground">/ bespoke</span>
                                </div>
                                <ul className="space-y-3 text-xs text-slate-600 dark:text-slate-300">
                                    <li className="flex items-center gap-2">✓ Unlimited Employees scale</li>
                                    <li className="flex items-center gap-2">✓ Dedicated MongoDB Server</li>
                                    <li className="flex items-center gap-2">✓ Customized API Integrations</li>
                                    <li className="flex items-center gap-2">✓ Enterprise SLA & Support</li>
                                    <li className="flex items-center gap-2">✓ Custom Invoicing options</li>
                                </ul>
                            </div>
                            <Button type="button" onClick={() => setModal("contact")} className="w-full rounded-full h-11 text-xs font-bold mt-8" variant="outline">
                                Contact Sales
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="relative z-10 px-6 lg:px-12 py-24 bg-black/[0.01] dark:bg-white/[0.01] border-t border-black/5 dark:border-white/5">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-3">Questions & Answers</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100">Legal, Security & Plan FAQ</h2>
                        <p className="text-xs text-muted-foreground mt-2">Everything you need to know about data compliance, billing verification, and technical support.</p>
                    </div>
                    
                    <div className="space-y-4">
                        {FAQS.map((faq, i) => (
                            <div 
                                key={i} 
                                className="glass rounded-2xl border border-white/10 dark:border-white/5 overflow-hidden transition-all duration-300"
                            >
                                <button
                                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                                    className="w-full px-6 py-4 flex items-center justify-between text-left font-semibold text-sm text-slate-800 dark:text-slate-200 hover:text-primary transition"
                                >
                                    <span>{faq.q}</span>
                                    <span className="text-xs text-muted-foreground">
                                        {activeFaq === i ? "−" : "+"}
                                    </span>
                                </button>
                                <AnimatePresence initial={false}>
                                    {activeFaq === i && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.25 }}
                                        >
                                            <div className="px-6 pb-5 pt-1 text-xs text-muted-foreground leading-relaxed border-t border-black/5 dark:border-white/5">
                                                {faq.a}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <footer className="relative z-10 bg-slate-950 text-slate-400 border-t border-slate-900 px-6 lg:px-12 py-16 text-sm">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Left Column - Founder & Contact */}
                    <div className="space-y-3.5 text-left">
                        <h4 className="font-heading font-bold text-white text-base tracking-tight font-semibold">ParshHR</h4>
                        <p className="text-xs leading-relaxed max-w-xs text-slate-500">
                            Modern HRMS & Employee Portal. Innovating workforce management.
                        </p>
                        <div className="space-y-1.5 text-xs text-slate-400 pt-2">
                            <div><strong className="text-slate-300">Founder:</strong> Gauransh Jaroli</div>
                            <div><strong className="text-slate-300">Address:</strong> Udaipur, Rajasthan, India</div>
                            <div><strong className="text-slate-300">Phone:</strong> +91 9521347419</div>
                            <div><strong className="text-slate-300">Email:</strong> admin@parshwebcraft.in</div>
                        </div>
                    </div>

                    {/* Center Column - Quick Links */}
                    <div className="space-y-4 text-left md:pl-8">
                        <h4 className="font-heading font-bold text-white text-base tracking-tight font-semibold">Quick Links</h4>
                        <ul className="space-y-2.5 text-xs">
                            <li>
                                <button type="button" onClick={() => setModal("privacy")} className="hover:text-white transition">Privacy Policy</button>
                            </li>
                            <li>
                                <button type="button" onClick={() => setModal("terms")} className="hover:text-white transition">Terms & Conditions</button>
                            </li>
                            <li>
                                <button type="button" onClick={() => setModal("contact")} className="hover:text-white transition">Contact Us</button>
                            </li>
                            <li>
                                <button type="button" onClick={() => setModal("support")} className="hover:text-white transition">Support Helpdesk</button>
                            </li>
                        </ul>
                    </div>

                    {/* Right Column - Map & Socials */}
                    <div className="space-y-4 text-left">
                        <h4 className="font-heading font-bold text-white text-base tracking-tight font-semibold">Get in Touch</h4>
                        
                        {/* Map Embed */}
                        <div className="w-full rounded-xl overflow-hidden shadow-md bg-slate-900 border border-slate-800">
                            <iframe 
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d116047.88603610086!2d73.6811252069796!3d24.5807971358941!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3967e56550a14411%3A0xdbd8c28455b868b0!2sUdaipur%2C%20Rajasthan!5e0!3m2!1sen!2sin!4v1719280000000!5m2!1sen!2sin" 
                                className="w-full h-24 border-0" 
                                allowFullScreen="" 
                                loading="lazy" 
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Udaipur, Rajasthan, India Map"
                            />
                        </div>

                        {/* Social Links */}
                        <div className="flex items-center gap-3 pt-2">
                            <a href="https://github.com/parshwebcraft" target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center border border-slate-800 transition">
                                <Github className="w-4 h-4" />
                            </a>
                            <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center border border-slate-800 transition">
                                <Linkedin className="w-4 h-4" />
                            </a>
                            <a href="https://twitter.com" target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center border border-slate-800 transition">
                                <Twitter className="w-4 h-4" />
                            </a>
                            <a href="https://instagram.com" target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center border border-slate-800 transition">
                                <Instagram className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto border-t border-slate-900 mt-12 pt-6 text-xs text-slate-500 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                    <div>© {new Date().getFullYear()} ParshHR by ParshWebCraft. All rights reserved.</div>
                    <div className="flex items-center gap-5 text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
                        <Link to="/careers" className="hover:text-white transition">Careers</Link>
                        <a href="#features" className="hover:text-white transition">Features</a>
                    </div>
                </div>
            </footer>

            {/* Privacy Modal */}
            <Dialog open={modal === "privacy"} onOpenChange={() => setModal(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Privacy Policy</DialogTitle>
                    </DialogHeader>
                    <div className="text-xs text-slate-600 dark:text-slate-300 space-y-4 pt-2 leading-relaxed max-h-[70vh] overflow-y-auto pr-2">
                        <p className="font-bold text-slate-800 dark:text-slate-200">Last Updated: August 8, 2026</p>
                        <p>At ParshHR, we prioritize the protection and security of your organization's and employees' personal information. This Privacy Policy details how we collect, store, process, and safeguard data in compliance with enterprise privacy standards.</p>
                        
                        <div className="space-y-1.5">
                            <h4 className="font-semibold text-slate-800 dark:text-slate-200">1. Data Scoping & Database Isolation</h4>
                            <p>To ensure absolute data confidentiality, we implement a strict **Database-per-Tenant** architecture. Your corporate directory, employee records, attendance locations, files, and chats reside on a physically separate database instance. We do not aggregate or co-mingle data across workspaces.</p>
                        </div>

                        <div className="space-y-1.5">
                            <h4 className="font-semibold text-slate-800 dark:text-slate-200">2. Geolocation & Presence Tracking</h4>
                            <p>Geofenced location coordinate logs are collected strictly when employees voluntarily perform clock-in or clock-out actions. ParshHR does not track active location or trace background coordinates when the app is closed or running in background. Session presence (Requester IP address, browser metadata, and activity timestamps) is captured only to prevent authentication breaches.</p>
                        </div>

                        <div className="space-y-1.5">
                            <h4 className="font-semibold text-slate-800 dark:text-slate-200">3. Information We Collect</h4>
                            <p>We process standard employee information (names, emails, phones, DOJ, salaries, designations) provided voluntarily by workspace admins. Password hashes are encrypted using secure modern hashing algorithms (bcrypt) on the backend; plaintext passwords are never stored or logged.</p>
                        </div>

                        <div className="space-y-1.5">
                            <h4 className="font-semibold text-slate-800 dark:text-slate-200">4. Third-Party Integrations</h4>
                            <p>We integrate with Razorpay (payment processing) and Resend (email delivery). Only essential metadata (such as business emails, transactions amounts, and order tokens) are transferred to execute checkout operations. We do not sell or monetize workspace metrics.</p>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Terms Modal */}
            <Dialog open={modal === "terms"} onOpenChange={() => setModal(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Terms & Conditions</DialogTitle>
                    </DialogHeader>
                    <div className="text-xs text-slate-600 dark:text-slate-300 space-y-4 pt-2 leading-relaxed max-h-[70vh] overflow-y-auto pr-2">
                        <p className="font-bold text-slate-800 dark:text-slate-200">Last Updated: August 8, 2026</p>
                        <p>By creating a corporate workspace or accessing ParshHR portals, you agree to comply with the following operational terms and conditions.</p>
                        
                        <div className="space-y-1.5">
                            <h4 className="font-semibold text-slate-800 dark:text-slate-200">1. Subscriptions & Billing Verification</h4>
                            <p>Workspace accounts start with a 14-day free trial. Scaling beyond 14 days or registering more than 10 active members requires updating to our Pro plan. The subscription cost is flat **₹5,000 + 18% GST (₹5,900.00 monthly)**. Payment checkouts are verified securely via Razorpay and recur monthly.</p>
                        </div>

                        <div className="space-y-1.5">
                            <h4 className="font-semibold text-slate-800 dark:text-slate-200">2. Usage Capping & Plan Enforcement</h4>
                            <p>Free trial limits are capped at 10 active employee slots. The Pro plan allows up to 100 active members (including employees and interns). Attempting to add, import, or reactivate personnel beyond your plan limit will be automatically blocked by the database enforcer until the subscription tier is upgraded.</p>
                        </div>

                        <div className="space-y-1.5">
                            <h4 className="font-semibold text-slate-800 dark:text-slate-200">3. Termination & Data Policy</h4>
                            <p>You may cancel your workspace subscription at any time. Upon trial expiration or plan cancellation, your workspace write-access will be restricted. Data remains securely archived for up to 90 days, enabling you to upgrade and recover all files, logs, and configurations cleanly.</p>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Contact Modal */}
            <Dialog open={modal === "contact"} onOpenChange={() => setModal(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Contact Us</DialogTitle>
                    </DialogHeader>
                    <div className="text-xs text-slate-600 dark:text-slate-300 space-y-3 pt-2 leading-relaxed">
                        <p>For custom software development, integrations, or operational queries, reach out directly to the founding team at ParshWebCraft:</p>
                        <div className="space-y-1.5 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800">
                            <div><strong>Founder:</strong> Gauransh Jaroli</div>
                            <div><strong>Phone:</strong> +91 9521347419</div>
                            <div><strong>Email:</strong> admin@parshwebcraft.in</div>
                            <div><strong>Address:</strong> Udaipur, Rajasthan, India</div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Support Modal */}
            <Dialog open={modal === "support"} onOpenChange={() => setModal(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Technical Support & SLA Policies</DialogTitle>
                    </DialogHeader>
                    <div className="text-xs text-slate-600 dark:text-slate-300 space-y-4 pt-2 leading-relaxed">
                        <p>Our priority technical support desk is dedicated to ensuring your workspace operates smoothly. Here are our response SLAs and engagement paths:</p>
                        
                        <div className="space-y-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
                            <div><strong>✉️ Primary Helpdesk:</strong> support@parshwebcraft.in</div>
                            <div><strong>⚡ Priority Support:</strong> Chat support available inside the workspace billing panel.</div>
                            <div><strong>📞 Phone Support:</strong> +91 9521347419 (Founder Escalation desk, available 10 AM - 7 PM IST)</div>
                        </div>

                        <div className="space-y-1.5">
                            <h4 className="font-semibold text-slate-800 dark:text-slate-200">Target Response Times (SLAs):</h4>
                            <ul className="space-y-1 list-disc pl-4">
                                <li><strong>Priority 1 (System Outage / Payment failure):</strong> Under 4 Hours.</li>
                                <li><strong>Priority 2 (Attendance geofencing issues / Document errors):</strong> Under 12 Hours.</li>
                                <li><strong>Priority 3 (General queries and workspace setup help):</strong> Under 24 Hours.</li>
                            </ul>
                        </div>
                        <p className="text-[10px] text-muted-foreground italic">Note: Live support escalations are exclusive to workspaces running on active Pro subscription tiers.</p>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DemoBookingCard() {
    const [form, setForm] = useState({ email: "", phone: "", company_size: "1-10", state: "Rajasthan" });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/public/book-demo", form);
            toast.success("Demo requested successfully! We'll contact you soon.");
            setForm({ email: "", phone: "", company_size: "1-10", state: "Rajasthan" });
        } catch (err) {
            toast.error("Failed to book demo. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-3xl p-6 sm:p-8 shadow-xl text-left border border-white/20 dark:border-white/5 relative"
        >
            <h3 className="font-heading font-bold text-xl text-slate-800 dark:text-slate-100">See ParshHR in action</h3>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                Get a personalised walkthrough tailored to your team size and operational needs.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                    <Label htmlFor="demo-email" className="text-xs font-semibold">Work Email <span className="text-destructive">*</span></Label>
                    <Input 
                        id="demo-email" 
                        type="email" 
                        required 
                        value={form.email} 
                        placeholder="you@company.com" 
                        onChange={(e) => setForm({ ...form, email: e.target.value })} 
                        className="mt-1 w-full h-10 text-xs rounded-xl"
                    />
                </div>
                <div>
                    <Label htmlFor="demo-phone" className="text-xs font-semibold">Phone Number <span className="text-destructive">*</span></Label>
                    <Input 
                        id="demo-phone" 
                        type="tel" 
                        required 
                        value={form.phone} 
                        placeholder="+91 98765 43210" 
                        onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                        className="mt-1 w-full h-10 text-xs rounded-xl"
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="demo-size" className="text-xs font-semibold">Company Size <span className="text-destructive">*</span></Label>
                        <select 
                            id="demo-size" 
                            value={form.company_size} 
                            onChange={(e) => setForm({ ...form, company_size: e.target.value })} 
                            className="mt-1 w-full h-10 text-xs rounded-xl border border-input bg-background px-3"
                        >
                            <option value="1-10">1-10</option>
                            <option value="11-50">11-50</option>
                            <option value="51-100">51-100</option>
                            <option value="101-500">101-500</option>
                            <option value="500+">500+</option>
                        </select>
                    </div>
                    <div>
                        <Label htmlFor="demo-state" className="text-xs font-semibold">State <span className="text-destructive">*</span></Label>
                        <select 
                            id="demo-state" 
                            value={form.state} 
                            onChange={(e) => setForm({ ...form, state: e.target.value })} 
                            className="mt-1 w-full h-10 text-xs rounded-xl border border-input bg-background px-3"
                        >
                            <option value="Rajasthan">Rajasthan</option>
                            <option value="Delhi">Delhi</option>
                            <option value="Maharashtra">Maharashtra</option>
                            <option value="Karnataka">Karnataka</option>
                            <option value="Gujarat">Gujarat</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                </div>

                <Button type="submit" className="w-full h-11 rounded-full text-xs font-bold mt-2" disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Book a Free Demo"}
                    {!loading && <ArrowRight className="w-3.5 h-3.5 ml-1.5" />}
                </Button>

                <div className="flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground pt-2">
                    <Check className="w-3.5 h-3.5 text-emerald-500" /> No credit card required
                </div>
            </form>
        </motion.div>
    );
}
